import React, { useState, useEffect } from 'react';

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );
}

export default function DrillCard({ question, result, onSelect, onNext, atEnd, disabled }) {
  const [showAll, setShowAll] = useState(false);
  const options = question.options || [];
  const answered = !!result;
  const selectedU = result ? (result.selected_answer || '').toUpperCase() : null;
  const correctU = result ? (result.correct_answer || '').toUpperCase() : null;

  const distractors = (result && result.distractor_misconceptions) || {};
  const correctExplanation = (result && result.explanation) || '';

  // Reset "show all" when the question changes
  useEffect(() => {
    setShowAll(false);
  }, [question.id]);

  const explanationFor = (letter) => {
    const letterU = (letter || '').toString().toUpperCase();
    if (letterU === correctU) return correctExplanation;
    return distractors[letter] || distractors[letterU] || '';
  };

  return (
    <div className="bg-bg-card rounded-r-xl border border-border-soft overflow-hidden mb-sp-4">
      <div className="px-sp-5 py-sp-3 border-b border-border-soft">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-accent truncate block">
          {question.general_topic || question.subject_area || ''}
        </span>
      </div>

      <div className="px-sp-6 py-sp-5">
        <p className="text-[15px] font-semibold text-text-primary mb-sp-4">
          {question.stem}
        </p>

        <div className="flex flex-col gap-sp-2">
          {options.map((opt) => {
            const letter = (opt.letter || '').toString();
            const letterU = letter.toUpperCase();
            const isCorrect = answered && letterU === correctU;
            const isWrong = answered && letterU === selectedU && selectedU !== correctU;
            const isDimmed = answered && !isCorrect && !isWrong;

            let wrapCls = 'border border-border-soft';
            let letterCls = 'bg-bg-surface text-text-muted';
            let letterContent = letter;

            if (isCorrect) {
              wrapCls = 'border-2 border-green bg-[rgba(16,185,129,0.03)]';
              letterCls = 'bg-green text-white';
              letterContent = <CheckIcon />;
            } else if (isWrong) {
              wrapCls = 'border-2 border-red bg-[rgba(239,68,68,0.03)]';
              letterCls = 'bg-red text-white';
              letterContent = <CrossIcon />;
            }

            const defaultExplanation = answered && (isCorrect || isWrong) ? explanationFor(letter) : '';
            const extraExplanation = answered && showAll && isDimmed ? explanationFor(letter) : '';

            return (
              <div key={letter}>
                <button
                  type="button"
                  onClick={() => {
                    if (disabled || answered) return;
                    onSelect(letter);
                  }}
                  disabled={disabled || answered}
                  className={`w-full flex items-center gap-sp-3 px-sp-4 py-sp-3 rounded-r-md text-left transition-colors bg-bg-card ${wrapCls} ${
                    answered ? 'cursor-default' : 'hover:border-border-medium cursor-pointer'
                  }`}
                >
                  <span
                    className={`w-[26px] h-[26px] rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${letterCls}`}
                  >
                    {letterContent}
                  </span>
                  <p className="text-sm text-text-primary leading-relaxed">
                    {opt.text}
                  </p>
                </button>

                {defaultExplanation && (
                  <div
                    className={`mt-sp-2 ml-[38px] px-sp-3 py-sp-2 rounded-[6px] text-sm text-text-primary leading-relaxed ${
                      isCorrect
                        ? 'border-l-[3px] border-green bg-[rgba(16,185,129,0.05)]'
                        : 'border-l-[3px] border-red bg-[rgba(239,68,68,0.03)]'
                    }`}
                  >
                    {defaultExplanation}
                  </div>
                )}

                {!defaultExplanation && extraExplanation && (
                  <div className="mt-sp-2 ml-[38px] px-sp-3 py-sp-2 rounded-[6px] text-sm text-text-primary leading-relaxed border-l-[3px] border-border-soft bg-bg-surface">
                    {extraExplanation}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {answered && (
        <div className="flex items-center justify-between gap-sp-3 px-sp-6 py-sp-3 border-t border-border-soft">
          {!showAll ? (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="text-xs font-semibold text-accent hover:text-accent-hover transition-colors"
            >
              Show all explanations
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={onNext}
            className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors"
          >
            {atEnd ? 'View summary' : 'Next drill'}
          </button>
        </div>
      )}
    </div>
  );
}
